import { request } from '@/utils/ajax';

export interface EmailContentListItem {
    name: string;
    languages: string[];
    sendsToBuyer: boolean;
    sendsToSeller: boolean;
    overrideCount: number;
    /**
     * Languages overridden at the requested scope (null entry = any language). Null when the list was
     * requested without a scope.
     */
    scopeOverrideLanguages: (string | null)[] | null;
    /** For a project scope: the owning profile's tier, i.e. what the project would inherit. */
    profileOverrideLanguages: (string | null)[] | null;
}

export interface EmailContentGlobal {
    id: number;
    languageCode: string | null;
    subject: string | null;
    template: string;
    sendsToBuyer: boolean | null;
    sendsToSeller: boolean | null;
    updateDate: string | null;
}

export interface EmailContentOverride {
    id: number;
    name: string;
    languageCode: string | null;
    projectId: number | null;
    profileId: number | null;
    /** Display name of the project or seller profile the override targets. */
    scopeName: string | null;
    subject: string;
    template: string;
    updateDate: string | null;
}

export interface EmailContentDetail {
    name: string;
    globals: EmailContentGlobal[];
    overrides: EmailContentOverride[];
}

export interface EmailLayoutGlobal {
    id: number;
    projectId: number | null;
    projectName: string | null;
    template: string | null;
    updateDate: string | null;
}

export interface EmailLayoutOverride {
    id: number;
    languageCode: string | null;
    projectId: number | null;
    profileId: number | null;
    scopeName: string | null;
    template: string;
    updateDate: string | null;
}

export interface EmailLayouts {
    /** Languages the platform's content templates exist in, for the language picker. */
    languages: string[];
    globals: EmailLayoutGlobal[];
    overrides: EmailLayoutOverride[];
}

export interface SaveContentOverrideRequest {
    name: string;
    languageCode: string | null;
    projectId: number | null;
    profileId: number | null;
    subject: string;
    template: string;
}

export interface SaveLayoutOverrideRequest {
    languageCode: string | null;
    projectId: number | null;
    profileId: number | null;
    template: string;
}

export type EmailPreviewKind = 'content' | 'layout';

export interface EmailPreviewRequest {
    kind: EmailPreviewKind;
    template: string;
    subject?: string;
    /** Set to preview content against an unsaved layout; omit to use the layout the project resolves to. */
    layoutTemplate?: string;
    projectId?: number | null;
    /** Resolves the wrapping layout for this seller profile when no project is chosen. */
    profileId?: number | null;
    languageCode?: string | null;
}

export interface EmailPreviewResult {
    html: string;
    subject: string;
    /** MJML validation messages. The HTML is still usable when this is non-empty. */
    warnings: string[];
}

/** Which tier of the resolution order an effective template came from; "none" means the send would fail. */
export type EmailTemplateSource = 'project-override' | 'profile-override' | 'global' | 'none';

/** The scope a template is being viewed or edited at. */
export interface TemplateScope {
    projectId: number | null;
    profileId: number | null;
    languageCode: string | null;
}

interface ResolutionBase {
    languageCode: string | null;
    projectId: number | null;
    projectName: string | null;
    /** The seller profile in effect — derived from the project when one is chosen. */
    profileId: number | null;
    profileName: string | null;
    source: EmailTemplateSource;
}

export interface EmailContentResolution extends ResolutionBase {
    name: string;
    /** What would actually be sent for this scope; null when no tier matches and the send would fail. */
    subject: string | null;
    template: string | null;
    /** Null when no override exists at exactly this scope and language, meaning a save would create one. */
    projectOverride: EmailContentOverride | null;
    profileOverride: EmailContentOverride | null;
    global: EmailContentGlobal | null;
}

export interface EmailLayoutResolution extends ResolutionBase {
    template: string | null;
    projectOverride: EmailLayoutOverride | null;
    profileOverride: EmailLayoutOverride | null;
    global: EmailLayoutGlobal | null;
}

/** Drops empty values so they are not sent as "null" strings. */
function scopeQuery(scope: TemplateScope): Record<string, string | number> {
    const query: Record<string, string | number> = {};
    if (scope.projectId) {
        query.projectId = scope.projectId;
    }
    if (scope.profileId) {
        query.profileId = scope.profileId;
    }
    if (scope.languageCode) {
        query.languageCode = scope.languageCode;
    }
    return query;
}

export function resolveEmailContent(name: string, scope: TemplateScope): Promise<EmailContentResolution> {
    return request(`api/email-templates/contents/${encodeURIComponent(name)}/resolve`, {
        query: scopeQuery(scope),
    }).then((x) => x.json());
}

export function resolveEmailLayout(scope: TemplateScope): Promise<EmailLayoutResolution> {
    return request('api/email-templates/layouts/resolve', { query: scopeQuery(scope) }).then((x) => x.json());
}

export function getEmailContents(scope?: {
    projectId?: number | null;
    profileId?: number | null;
}): Promise<EmailContentListItem[]> {
    const query: Record<string, number> = {};
    if (scope?.projectId) {
        query.projectId = scope.projectId;
    }
    if (scope?.profileId) {
        query.profileId = scope.profileId;
    }
    return request('api/email-templates/contents', { query }).then((x) => x.json());
}

/** Projects and profiles that have any override, for the scope pickers' badges. */
export interface EmailOverrideScopes {
    projectIds: number[];
    profileIds: number[];
}

export function getEmailOverrideScopes(): Promise<EmailOverrideScopes> {
    return request('api/email-templates/override-scopes').then((x) => x.json());
}

export function getEmailContent(name: string): Promise<EmailContentDetail> {
    return request(`api/email-templates/contents/${encodeURIComponent(name)}`).then((x) => x.json());
}

export function createContentOverride(req: SaveContentOverrideRequest): Promise<EmailContentOverride> {
    return request('api/email-templates/content-overrides', { json: true, body: req, method: 'POST' }).then((x) =>
        x.json()
    );
}

export function updateContentOverride(
    id: number,
    req: { subject: string; template: string }
): Promise<EmailContentOverride> {
    return request(`api/email-templates/content-overrides/${id}`, { json: true, body: req, method: 'PUT' }).then((x) =>
        x.json()
    );
}

export function deleteContentOverride(id: number): Promise<Response> {
    return request(`api/email-templates/content-overrides/${id}`, { method: 'DELETE' });
}

export function getEmailLayouts(): Promise<EmailLayouts> {
    return request('api/email-templates/layouts').then((x) => x.json());
}

export function createLayoutOverride(req: SaveLayoutOverrideRequest): Promise<EmailLayoutOverride> {
    return request('api/email-templates/layout-overrides', { json: true, body: req, method: 'POST' }).then((x) =>
        x.json()
    );
}

export function updateLayoutOverride(id: number, req: { template: string }): Promise<EmailLayoutOverride> {
    return request(`api/email-templates/layout-overrides/${id}`, { json: true, body: req, method: 'PUT' }).then((x) =>
        x.json()
    );
}

export function deleteLayoutOverride(id: number): Promise<Response> {
    return request(`api/email-templates/layout-overrides/${id}`, { method: 'DELETE' });
}

export function previewEmail(req: EmailPreviewRequest): Promise<EmailPreviewResult> {
    return request('api/email-templates/preview', { json: true, body: req, method: 'POST' }).then((x) => x.json());
}

export interface EmailScopeOverride {
    name: string;
    languageCode: string | null;
}

/** Overrides at exactly one scope, for the dashboards' email templates card. */
export interface EmailTemplateScopeSummary {
    /** Content templates overridden at this scope, one entry per language variant. */
    contentOverrides: EmailScopeOverride[];
    /** Language codes of the layout overrides at this scope; null means "any language". */
    layoutOverrideLanguages: (string | null)[];
    /** The tier this scope's layout actually resolves to. */
    layoutSource: EmailTemplateSource;
}

export function getEmailTemplateScopeSummary(scope: {
    projectId?: number;
    profileId?: number;
}): Promise<EmailTemplateScopeSummary> {
    const query: Record<string, number> = {};
    if (scope.projectId) {
        query.projectId = scope.projectId;
    }
    if (scope.profileId) {
        query.profileId = scope.profileId;
    }
    return request('api/email-templates/scope-summary', { query }).then((x) => x.json());
}
